/**
 * ValidationReportDisplay Component
 * Displays regulations from validation report JSON files in the grouped folder
 * Loads all files from manifest.json and shows only visible regulations
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  loadComplianceReportsForModel,
  fetchJSONFromGCS,
} from "../../services/dataService";
import { useViewerStore } from "../../store/viewerStore";
import { getModelRegulationDefinitionFiles, getModelConfig } from "../../config/models";

interface RegulationResult {
  regulation_id: string;
  title: string;
  requirement: string;
  status: string;
  measured_value?: string | number;
  required_value?: string;
  min_value?: number;
  max_value?: number;
  optimal_value?: number;
  unit?: string;
  notes?: string;
  validation_type?: string;
  visible?: boolean;
  entities_used?: Array<{
    dbId?: number;
    name?: string;
    type?: string;
    role?: string;
  }>;
  [key: string]: unknown;
}

interface ValidationReport {
  validation_metadata?: {
    room_id?: string | number;
    room_name?: string;
    standard?: string;
    jurisdiction?: string;
    section?: string;
    validation_date?: string;
  };
  regulation_validation_results?: RegulationResult[];
  validation_results?: RegulationResult[];
  regulations?: RegulationResult[];
  // Door reports use rule_validations format (will be transformed but handle as fallback)
  rule_validations?: Array<{
    rule_number?: string;
    rule_name?: string;
    rule_raw_text?: string;
    status?: string;
    measured_value?: string | number;
    actual_value?: string | number;
    required_value?: string;
    unit?: string;
    llm_reasoning?: string;
    llm_notes?: string[] | string;
    details?: string;
    used_tool_measurement?: boolean;
    involved_db_ids?: number[];
    confidence?: string;
    warning_type?: string;
    warning_message?: string;
  }>;
}

// Extract parent regulation ID (e.g., "604" from "604.2.1")
const getParentRegulationId = (regulationId: string): string => {
  const match = regulationId.match(/^(\d+)/);
  return match ? match[1] : regulationId;
};

// Strip text after and including underscore from regulation ID for display.
// For skylight, also strip the chapter prefix so "100.1" → "1".
const formatRegulationIdForDisplay = (regulationId: string, modelId?: string): string => {
  let id = regulationId;
  const underscoreIndex = id.indexOf("_");
  if (underscoreIndex !== -1) {
    id = id.substring(0, underscoreIndex);
  }
  if (modelId === 'skylight') {
    const dotIndex = id.indexOf(".");
    if (dotIndex !== -1) {
      return id.substring(dotIndex + 1);
    }
  }
  return id;
};

// Extract subsection ID (e.g., "604.2" from "604.2.1")
const getSubsectionId = (regulationId: string): string => {
  const parts = regulationId.split(".");
  if (parts.length >= 2) {
    return `${parts[0]}.${parts[1]}`;
  }
  return getParentRegulationId(regulationId);
};

// Natural sort function for regulation IDs
const naturalSort = (a: string, b: string): number => {
  const aParts = a.split(".").map((part) => {
    const num = parseInt(part, 10);
    return isNaN(num) ? part : num;
  });
  const bParts = b.split(".").map((part) => {
    const num = parseInt(part, 10);
    return isNaN(num) ? part : num;
  });

  const maxLength = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < maxLength; i++) {
    const aPart = aParts[i] ?? 0;
    const bPart = bParts[i] ?? 0;

    if (typeof aPart === "number" && typeof bPart === "number") {
      if (aPart !== bPart) {
        return aPart - bPart;
      }
    } else if (typeof aPart === "number") {
      return -1;
    } else if (typeof bPart === "number") {
      return 1;
    } else {
      const comparison = String(aPart).localeCompare(String(bPart));
      if (comparison !== 0) {
        return comparison;
      }
    }
  }
  return 0;
};

// Get section title from first regulation in section
const getSectionTitle = (regulations: RegulationResult[], modelId?: string): string => {
  if (regulations.length === 0) return "";
  const firstReg = regulations[0];
  const parentId = getParentRegulationId(firstReg.regulation_id);

  // Skylight: use the chapter_title stored in the regulation data
  if (modelId === 'skylight') {
    const chapterTitle = (firstReg as any).chapter_title;
    return chapterTitle || "Standards";
  }

  // Try to find a meaningful title from the regulations
  // For "1103", return "Chapter 1103 - Type A units"
  if (parentId === "1103") {
    return "Chapter 1103 - Type A units";
  }
  // For "603", Toilet and Bathing Rooms
  if (parentId === "603") {
    return "Chapter 603 - Toilet and Bathing Rooms";
  }
  // For "604", we know it's about Water Closets
  if (parentId === "604") {
    return "Chapter 604 - Water Closets and Toilet Compartments";
  }
  // For "606", Lavatories and Sinks
  if (parentId === "606") {
    return "Chapter 606 - Lavatories and Sinks";
  }
  // For "609", Grab Bars
  if (parentId === "609") {
    return "Chapter 609 - Grab Bars";
  }
  // For "404", return Chapter 404 title
  if (parentId === "404") {
    return "Chapter 404 - Doors, Doorways and Gates";
  }

  return `${parentId} - ${firstReg.title || "Regulations"}`;
};

// Get subsection title - accepts optional definitions map for official titles
const getSubsectionTitle = (
  regulations: RegulationResult[],
  definitions?: Map<string, { id: string; title: string; requirement?: string }>,
  modelId?: string
): string => {
  if (regulations.length === 0) return "";
  const firstReg = regulations[0];
  const subsectionId = getSubsectionId(firstReg.regulation_id);

  // Skylight: use the formatted subsection id (strips chapter prefix) + rule title
  if (modelId === 'skylight') {
    const displayId = formatRegulationIdForDisplay(subsectionId, modelId);
    const titles = regulations.map((r) => r.title).filter(Boolean);
    return titles.length > 0 ? `${displayId} - ${titles[0]}` : displayId;
  }

  // First, try to get the official title from regulation definitions (e.g., "404.2" -> "Manual doors, doorways and manual gates")
  if (definitions?.has(subsectionId)) {
    const def = definitions.get(subsectionId);
    if (def?.title) {
      return `${subsectionId} - ${def.title}`;
    }
  }

  // Fallback: infer title from regulation titles
  const titles = regulations.map((r) => r.title).filter(Boolean);
  if (titles.length > 0) {
    return `${subsectionId} - ${titles[0]}`;
  }

  return subsectionId;
};

interface RegulationDefinition {
  id: string;
  title: string;
  requirement?: string;
}

export function ValidationReportDisplay() {
  const { selectedModelId, selectedJurisdiction, viewer } = useViewerStore();
  const [reports, setReports] = useState<ValidationReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regulationDefinitions, setRegulationDefinitions] = useState<
    Map<string, RegulationDefinition>
  >(new Map());
  const [ada404Regulations, setAda404Regulations] = useState<
    RegulationResult[]
  >([]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  const [expandedSubsections, setExpandedSubsections] = useState<Set<string>>(
    new Set()
  );
  const [expandedRegulations, setExpandedRegulations] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    // Reset state when model changes
    setReports([]);
    setRegulationDefinitions(new Map());
    setAda404Regulations([]);
    setExpandedSections(new Set());
    setExpandedSubsections(new Set());
    setExpandedRegulations(new Set());
    setError(null);

    loadReports();
    loadRegulationDefinitions();
    loadAda404Regulations();
  }, [selectedModelId, selectedJurisdiction]);

  const loadRegulationDefinitions = async () => {
    try {
      const definitions = new Map<string, RegulationDefinition>();
      const defFiles = getModelRegulationDefinitionFiles(selectedModelId, selectedJurisdiction);

      // Load ada_604 regulation definitions (model-specific)
      if (defFiles.ada604) try {
        const ada604Response = await fetch(
          `/resources/${defFiles.ada604}`
        );
        if (ada604Response.ok) {
          const ada604Data = await ada604Response.json();
          if (ada604Data.ada604) {
            Object.keys(ada604Data.ada604).forEach((key) => {
              const section = ada604Data.ada604[key];
              if (section.title) {
                definitions.set(key, {
                  id: key,
                  title: section.title,
                  requirement: section.description,
                });
              }
            });
          }
        }
      } catch (error) {
        console.error("Error loading ada_604 definitions:", error);
      }

      // Load ADA_404 regulation definitions (model-specific)
      if (defFiles.ada404) try {
        const ada404Response = await fetch(
          `/resources/${defFiles.ada404}`
        );
        if (ada404Response.ok) {
          const ada404Data = await ada404Response.json();
          if (ada404Data.regulations) {
            ada404Data.regulations.forEach((reg: any) => {
              if (reg.ruleNumber) {
                // Extract title from fullText (first part before period)
                const titleMatch = reg.fullText?.match(/^[\d.]+ (.+?)(\.|$)/);
                const title = titleMatch
                  ? titleMatch[1].trim()
                  : reg.ruleNumber;

                definitions.set(reg.ruleNumber, {
                  id: reg.ruleNumber,
                  title: title,
                  requirement: reg.fullText,
                });
              }
            });
          }
        }
      } catch (error) {
        console.error("Error loading ada_404 definitions:", error);
      }

      // Load generic regulation definitions (keyed by regulation_id)
      if (defFiles.generic) try {
        const genericResponse = await fetch(
          `/resources/${defFiles.generic}`
        );
        if (genericResponse.ok) {
          const genericData = await genericResponse.json();
          Object.keys(genericData).forEach((key) => {
            const entry = genericData[key];
            if (entry.title) {
              definitions.set(key, {
                id: key,
                title: entry.title,
                requirement: entry.description || entry.ruleText || "",
              });
            }
          });
        }
      } catch (error) {
        console.error("Error loading generic definitions:", error);
      }

      // Add 1103 definitions manually
      definitions.set("1103.11", {
        id: "1103.11",
        title: "Toilet and bathing facilities",
        requirement: "1103.11 Toilet and bathing facilities.",
      });

      setRegulationDefinitions(definitions);
    } catch (error) {
      console.error("Error loading regulation definitions:", error);
    }
  };

  const loadAda404Regulations = async () => {
    try {
      const defFiles = getModelRegulationDefinitionFiles(selectedModelId, selectedJurisdiction);
      if (!defFiles.ada404) {
        setAda404Regulations([]);
        return;
      }
      const ada404Data = await fetchJSONFromGCS<{ regulations: any[] }>(
        defFiles.ada404
      );
      if (ada404Data) {
        if (ada404Data.regulations) {
          const regulations: RegulationResult[] = ada404Data.regulations.map(
            (reg: any) => {
              // Extract title from fullText (first part before period)
              const titleMatch = reg.fullText?.match(/^[\d.]+ (.+?)(\.|$)/);
              const title = titleMatch ? titleMatch[1].trim() : reg.ruleNumber;

              return {
                regulation_id: reg.ruleNumber,
                title: title,
                requirement: reg.fullText || "",
                status: "INFO", // Default status since these are definitions, not validation results
                visible: true,
                validation_type: "definition",
              };
            }
          );

          setAda404Regulations(regulations);
        }
      }
    } catch (error) {
      console.error("Error loading ADA_404_doors.json regulations:", error);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if model has any report files configured
      const { getModelConfigForJurisdiction } = await import('@/config/models');
      const modelConfig = getModelConfigForJurisdiction(selectedModelId, selectedJurisdiction);
      if (!modelConfig || modelConfig.reportSets.length === 0) {
        setReports([]);
        setLoading(false);
        return;
      }

      const reports = await loadComplianceReportsForModel<ValidationReport>(selectedModelId, selectedJurisdiction);

      if (reports.length === 0) {
        throw new Error("No valid reports found");
      }

      setReports(reports);

      // All accordions start closed by default
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load regulations"
      );
      console.error("Error loading reports:", err);
    } finally {
      setLoading(false);
    }
  };

  // Aggregate all regulations from all reports
  const allRegulations = useMemo(() => {
    const aggregated: RegulationResult[] = [];
    reports.forEach((report) => {
      // Handle v4 format (regulation_validation_results), v3 format (validation_results), old format (regulations),
      // and door report format (rule_validations - should be transformed but handle as fallback)
      let regulations =
        report.regulation_validation_results ||
        report.validation_results ||
        report.regulations ||
        [];
      
      // Fallback: if report has rule_validations (door reports that weren't transformed), convert them
      if (regulations.length === 0 && report.rule_validations) {
        regulations = (report.rule_validations || []).map((rule: any) => {
          let status = "WARNING";
          if (rule.status === "PASS") {
            status = "PASS";
          } else if (rule.status === "FAIL") {
            status = "FAIL";
          } else if (rule.status === "NOT_TESTED" || rule.status === "INSUFFICIENT_DATA") {
            status = "WARNING";
          } else if (rule.status === "NOT_APPLICABLE") {
            status = "INFO";
          }

          return {
            regulation_id: rule.rule_number || "",
            title: rule.rule_name || rule.rule_number || "",
            requirement: rule.rule_raw_text || "",
            status: status,
            measured_value: rule.measured_value || rule.actual_value,
            required_value: rule.required_value,
            unit: rule.unit,
            notes: rule.llm_reasoning || (Array.isArray(rule.llm_notes) ? rule.llm_notes.join(" ") : rule.llm_notes) || rule.details || "",
            validation_type: rule.used_tool_measurement ? "measurement" : "spatial_analysis",
            visible: rule.status !== "NOT_APPLICABLE",
            entities_used: rule.involved_db_ids?.map((dbId: number) => ({ dbId })) || [],
            confidence: rule.confidence,
            warning_type: rule.warning_type,
            warning_message: rule.warning_message,
            room_name: rule.room_name || (report as any).room_name || '',
          };
        });
      }
      
      aggregated.push(...regulations);
    });
    return aggregated;
  }, [reports]);

  // Filter and group regulations from all reports
  const groupedRegulations = useMemo(() => {
    // Filter to only visible regulations; for Dutch Bros also exclude INFO status
    // BNE: explicit allowlist of regulations to show (overrides visible flag)
    const bneAllowedPrefixes = [
      '604.2.2',
      '604.5.1.1', '604.5.1.2.3', '604.5.2',
      '1103.11.1', '1103.11.2.4.1', '1103.11.2.4.4',
    ];
    const isBneAllowed = (regId: string) =>
      bneAllowedPrefixes.some(prefix => regId === prefix || regId.startsWith(prefix + '.') || regId.startsWith(prefix + '_'));

    const visibleRegs = allRegulations.filter((reg) => {
      if (selectedModelId === 'bne') {
        return isBneAllowed(reg.regulation_id);
      }
      return reg.visible === true &&
        !(selectedModelId === 'dutch-bros' && reg.status === 'INFO') &&
        !(selectedModelId === 'dutch-bros' && !selectedJurisdiction?.toLowerCase().includes('florida') && (reg.regulation_id === '404.2.4.3' || reg.regulation_id === '404.2.5')) &&
        !(selectedModelId === 'dutch-bros' && selectedJurisdiction?.toLowerCase().includes('florida') && (reg.regulation_id === '404.2.5' || reg.regulation_id === '404.2.3' || reg.regulation_id === '606.4'));
    });

    // De-duplicate by regulation_id across all reports so each regulation
    // appears only once in the Compliance Hub (even if repeated per unit)
    const uniqueById = new Map<string, RegulationResult>();
    visibleRegs.forEach((reg) => {
      const key = reg.regulation_id;
      if (!uniqueById.has(key)) {
        uniqueById.set(key, reg);
      }
    });

    // For Dutch Bros Texas, inject missing rules from regulation definitions
    // These rules exist in the definitions file or checklist but not in scan results
    // (e.g., INFO-only in plumbing, or 0 rows in cloud scan)
    if (selectedModelId === 'dutch-bros' && !selectedJurisdiction?.toLowerCase().includes('florida')) {
      // 404.2.6 is not in the definitions file, so provide it manually from the checklist
      const manualDefs: Record<string, { title: string; requirement: string }> = {
        '404.2.6': {
          title: 'Door and Gate Hardware',
          requirement: '404.2.6 Door and Gate Hardware. Handles, pulls, latches, locks and other operable parts shall have a shape that is easy to grasp with one hand and does not require tight grasping, pinching or twisting of the wrist. 404.2.6.1 Hardware Height: Operable parts shall be 34 inches (865 mm) minimum and 48 inches (1220 mm) maximum above the floor.',
        },
      };
      const missingRuleIds = ['404.2.6', '603.2.1', '604.4', '604.7.1'];
      missingRuleIds.forEach((ruleId) => {
        if (!uniqueById.has(ruleId)) {
          const def = regulationDefinitions.get(ruleId) || manualDefs[ruleId];
          if (def) {
            uniqueById.set(ruleId, {
              regulation_id: ruleId,
              title: def.title,
              requirement: def.requirement || '',
              status: 'PASS',
              visible: true,
            } as RegulationResult);
          }
        }
      });
    }

    // BNE: inject missing regulations and collapse sub-rules into parent entries
    if (selectedModelId === 'bne') {
      // Collapse 604.5.1.1.* into single "604.5.1.1" and 604.5.2.* into "604.5.2"
      const collapseGroups = [
        { prefix: '604.5.1.1', title: 'Fixed side-wall grab bars', requirement: 'Side wall horizontal grab bar: 42 inches minimum length, located 12 inches maximum from rear wall, extending 54 inches minimum from rear wall.' },
        { prefix: '604.5.2', title: 'Rear-wall grab bars', requirement: 'Rear wall grab bar: 36 inches minimum length, located 12 inches maximum from side wall, extending 24 inches minimum from side wall.' },
      ];
      for (const group of collapseGroups) {
        const children = Array.from(uniqueById.entries()).filter(([id]) =>
          id.startsWith(group.prefix + '.'));
        if (children.length > 0) {
          // Use worst status from children
          const worstStatus = children.some(([, r]) => r.status === 'FAILED' || r.status === 'FAIL') ? 'FAILED'
            : children.some(([, r]) => r.status === 'WARNING') ? 'WARNING' : 'PASS';
          // Remove children
          children.forEach(([id]) => uniqueById.delete(id));
          // Add collapsed parent
          uniqueById.set(group.prefix, {
            regulation_id: group.prefix,
            title: group.title,
            requirement: group.requirement,
            status: worstStatus,
            visible: true,
          } as RegulationResult);
        }
      }
    }

    const uniqueRegs = Array.from(uniqueById.values());

    // Group by parent regulation (e.g., "604")
    const grouped: Record<string, Record<string, RegulationResult[]>> = {};

    uniqueRegs.forEach((reg) => {
      const parentId = getParentRegulationId(reg.regulation_id);
      const subsectionId = getSubsectionId(reg.regulation_id);

      if (!grouped[parentId]) {
        grouped[parentId] = {};
      }
      if (!grouped[parentId][subsectionId]) {
        grouped[parentId][subsectionId] = [];
      }
      grouped[parentId][subsectionId].push(reg);
    });

    // Sort regulations within each subsection
    Object.keys(grouped).forEach((parentId) => {
      Object.keys(grouped[parentId]).forEach((subsectionId) => {
        grouped[parentId][subsectionId].sort((a, b) =>
          naturalSort(a.regulation_id, b.regulation_id)
        );
      });
    });

    return grouped;
  }, [allRegulations, regulationDefinitions, selectedModelId, selectedJurisdiction]);

  // Group 404 regulations from ADA_404_doors.json
  const grouped404Regulations = useMemo(() => {
    const grouped: Record<string, RegulationResult[]> = {};

    // BNE: only show regulations with actual validation results
    const bneAllowed404 = new Set(['404.2.2', '404.2.3.2']);
    const filtered404 = selectedModelId === 'bne'
      ? ada404Regulations.filter(reg => bneAllowed404.has(reg.regulation_id))
      : ada404Regulations;

    filtered404.forEach((reg) => {
      const subsectionId = getSubsectionId(reg.regulation_id);

      if (!grouped[subsectionId]) {
        grouped[subsectionId] = [];
      }
      grouped[subsectionId].push(reg);
    });

    // Sort regulations within each subsection
    Object.keys(grouped).forEach((subsectionId) => {
      grouped[subsectionId].sort((a, b) =>
        naturalSort(a.regulation_id, b.regulation_id)
      );
    });

    return grouped;
  }, [ada404Regulations]);

  const toggleSection = (sectionKey: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionKey)) {
      newExpanded.delete(sectionKey);
    } else {
      newExpanded.add(sectionKey);
    }
    setExpandedSections(newExpanded);
  };

  const toggleSubsection = (subsectionKey: string) => {
    const newExpanded = new Set(expandedSubsections);
    if (newExpanded.has(subsectionKey)) {
      newExpanded.delete(subsectionKey);
    } else {
      newExpanded.add(subsectionKey);
    }
    setExpandedSubsections(newExpanded);
  };

  const toggleRegulation = (regulationId: string) => {
    const newExpanded = new Set(expandedRegulations);
    if (newExpanded.has(regulationId)) {
      newExpanded.delete(regulationId);
    } else {
      newExpanded.add(regulationId);
    }
    setExpandedRegulations(newExpanded);
  };

  const handleViewElement = (reg: RegulationResult) => {
    if (!viewer || !reg.entities_used?.length) return;
    const dbIds = reg.entities_used
      .map((e) => e.dbId)
      .filter((id): id is number => id !== undefined);
    if (dbIds.length === 0) return;
    viewer.isolate(dbIds);
    viewer.fitToView(dbIds);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Loading regulations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold mb-2">
          Error Loading Regulations
        </h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadReports}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (reports.length === 0) {
    const modelConfig = getModelConfig(selectedModelId);
    return (
      <div className="space-y-4 overflow-x-hidden">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Before you start, here's what you're about to see:
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            {selectedModelId === 'dutch-bros'
              ? "For retail developers, permitting delays cost revenue. HeroCon replaces the traditional 1-2 week compliance review with a scan that takes hours - so issues surface at design, not at inspection. We give you confidence for opening day."
              : "Traditional company standards reviews are done manually and take weeks. With Hero, it takes hours. We eliminate the back-and-forth review cycle, allowing you to enforce your standards proactively while significantly reducing operational costs. We save you time; you save money."}
          </p>
        </div>
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg font-medium mb-2">No compliance reports available</p>
          <p className="text-sm">
            {modelConfig?.name || 'This model'} does not have compliance reports yet.
          </p>
        </div>
      </div>
    );
  }

  // Helper: manual regulations list (IDs we want to show under "Manual Regulations")
  // Order: all 1103 first, then 604, then 404
  const manualRegulationIds: string[] = [
    "1103.11.1",
    "1103.11.2.4.1",
    "1103.11.2.4.4",
    "604.2",
    "604.5",
    "404.2.2",
    "404.2.3.2",
  ];

  // Helper: find title for a given manual regulation ID from aggregated regulations
  const getManualRegulationTitle = (regulationId: string): string | null => {
    // First, try exact match or prefix before underscore (e.g., 1103.11.2.4.4_lavatory_exception)
    let match = allRegulations.find((reg) => {
      const baseId = reg.regulation_id.split("_")[0];
      return baseId === regulationId && reg.visible === true;
    });

    // If no exact match, try to find a sub-regulation that starts with this ID
    // (e.g., for "604.2", find "604.2.1" or "604.2.2")
    if (!match) {
      match = allRegulations.find((reg) => {
        const baseId = reg.regulation_id.split("_")[0];
        return baseId.startsWith(regulationId + ".") && reg.visible === true;
      });
    }

    // If found in validation reports, return that title
    if (match) {
      return match.title ?? null;
    }

    // If not found, try to get from regulation definitions (ada_604_jersey.json or ADA_404_doors.json)
    const definition = regulationDefinitions.get(regulationId);
    if (definition) {
      return definition.title;
    }

    // If still no match, use hardcoded fallback for known sections
    const parentSectionTitles: Record<string, string> = {
      "604.2": "Water closet positioning",
      "604.5": "Grab bars",
      "404.2.2": "Clear Width",
      "404.2.3.2": "Swinging Door Maneuvering Clearances",
    };
    return parentSectionTitles[regulationId] || null;
  };

  // Sort parent IDs numerically
  const parentIds = Object.keys(groupedRegulations).sort(naturalSort);

  return (
    <div className="space-y-4 overflow-x-hidden">
      {/* Information Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Before you start, here's what you're about to see:
        </h3>
        <p className="text-sm text-gray-700 leading-relaxed">
          {selectedModelId === 'dutch-bros'
            ? "For retail developers, permitting delays cost revenue. HeroCon replaces the traditional 1-2 week compliance review with a scan that takes hours - so issues surface at design, not at inspection. We give you confidence for opening day."
            : "Traditional company standards reviews are done manually and take weeks. With Hero, it takes hours. We eliminate the back-and-forth review cycle, allowing you to enforce your standards proactively while significantly reducing operational costs. We save you time; you save money."}
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {/* Regulation flagged by our model - renders all relevant sections */}
        {parentIds.length > 0 && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              {selectedModelId === 'skylight' ? 'Standards flagged by our model' : 'Regulation flagged by our model'}
              <div className="relative group">
                <svg
                  className="w-4 h-4 text-blue-500 cursor-help"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[300px] max-w-[480px] z-50">
                  A list of relevant findings that our model flagged
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            </h2>
            {selectedModelId === 'dutch-bros' && (
              <p className="text-sm text-gray-600 mb-3">
                {selectedJurisdiction?.toLowerCase().includes('florida') ? 'FBC 2023' : 'ICC A 117.1'}
              </p>
            )}
            {/* Skylight: flat list without accordions */}
            {selectedModelId === 'skylight' ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {parentIds.flatMap((parentId) =>
                  Object.values(groupedRegulations[parentId]).flat()
                ).map((reg, idx, arr) => (
                  <div
                    key={`${reg.regulation_id}-${idx}`}
                    className={`px-4 py-3 ${idx < arr.length - 1 ? 'border-b border-gray-200' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-sm font-mono text-black font-semibold mt-0.5 min-w-[3rem]">
                        {reg.regulation_id}
                      </span>
                      <div className="flex-1">
                        <div className="text-sm text-black font-medium">
                          {reg.title || "No title"}
                        </div>
                        {reg.requirement && (
                          <div className="text-sm text-gray-600 mt-1">
                            {reg.requirement}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            /* Render all regulation sections under this heading */
            parentIds.map((parentId) => {
              const subsections = groupedRegulations[parentId];
              const subsectionIds = Object.keys(subsections).sort(naturalSort);
              const sectionId = `section-${parentId}`;
              const isExpanded = expandedSections.has(sectionId);
              const allRegsInSection = Object.values(subsections).flat();
              const sectionTitle = getSectionTitle(allRegsInSection, selectedModelId);

              return (
                <div key={parentId} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-3">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleSection(sectionId)}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors"
                      >
                        {isExpanded ? (
                          <svg
                            className="w-4 h-4 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        )}
                        <span>{sectionTitle}</span>
                        <span className="text-xs text-gray-500 font-normal">
                          ({allRegsInSection.length} regulations)
                        </span>
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 py-4 space-y-4">
                      {/* Subsections */}
                      {subsectionIds.map((subsectionId) => {
                        const regulations = subsections[subsectionId];
                        const subsectionKey = `${sectionId}-${subsectionId}`;
                        const isSubsectionExpanded =
                          expandedSubsections.has(subsectionKey);
                        const subsectionTitle = getSubsectionTitle(regulations, regulationDefinitions, selectedModelId);

                        return (
                          <div
                            key={subsectionId}
                            className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden"
                          >
                            <button
                              onClick={() => toggleSubsection(subsectionKey)}
                              className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 flex items-center gap-2 transition-colors text-left"
                            >
                              {isSubsectionExpanded ? (
                                <svg
                                  className="w-4 h-4 text-gray-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-4 h-4 text-gray-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              )}
                              <span className="text-sm font-semibold text-gray-900">
                                {subsectionTitle}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({regulations.length} regulations)
                              </span>
                            </button>

                            {isSubsectionExpanded && (
                              <div className="p-3 space-y-2">
                                {regulations.map((reg) => {
                                  const isRegExpanded = expandedRegulations.has(
                                    reg.regulation_id
                                  );

                                  return (
                                    <div
                                      key={reg.regulation_id}
                                      className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                                    >
                                      <button
                                        onClick={() =>
                                          toggleRegulation(reg.regulation_id)
                                        }
                                        className="w-full px-3 py-2 bg-white hover:bg-gray-50 flex items-center gap-2 transition-colors text-left"
                                      >
                                        {isRegExpanded ? (
                                          <svg
                                            className="w-4 h-4 text-gray-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M19 9l-7 7-7-7"
                                            />
                                          </svg>
                                        ) : (
                                          <svg
                                            className="w-4 h-4 text-gray-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M9 5l7 7-7 7"
                                            />
                                          </svg>
                                        )}
                                        <span className="text-xs font-mono text-gray-600">
                                          {formatRegulationIdForDisplay(
                                            reg.regulation_id,
                                            selectedModelId
                                          )}
                                        </span>
                                        <span className="text-sm font-medium text-gray-900 flex-1">
                                          {reg.title || "No title"}
                                        </span>
                                        {selectedModelId !== 'dutch-bros' && selectedModelId !== 'bne' && reg.status && reg.status !== "INFO" && (
                                          <span
                                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                              reg.status === "PASS"
                                                ? "bg-green-100 text-green-800"
                                                : reg.status === "FAILED"
                                                ? "bg-red-100 text-red-800"
                                                : reg.status === "WARNING"
                                                ? "bg-yellow-100 text-yellow-800"
                                                : "bg-gray-100 text-gray-600"
                                            }`}
                                          >
                                            {reg.status}
                                          </span>
                                        )}
                                        {selectedModelId !== 'dutch-bros' && selectedModelId !== 'bne' && reg.entities_used && reg.entities_used.length > 0 && reg.entities_used.some(e => e.dbId) && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewElement(reg);
                                            }}
                                            className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-0.5 rounded hover:bg-blue-50 transition-colors"
                                            title="View element in 3D"
                                          >
                                            View
                                          </button>
                                        )}
                                      </button>

                                      {isRegExpanded && (
                                        <div className="p-3 bg-gray-50 border-t border-gray-200 space-y-2">
                                          <div className="text-xs text-gray-700">
                                            <span className="font-medium">
                                              Requirement:
                                            </span>{" "}
                                            {reg.requirement}
                                          </div>
                                          {selectedModelId !== 'dutch-bros' && selectedModelId !== 'bne' && reg.measured_value !== undefined && (
                                            <div className="text-xs text-gray-700">
                                              <span className="font-medium">Measured:</span>{" "}
                                              {String(reg.measured_value)}
                                              {reg.unit ? ` ${reg.unit}` : ""}
                                              {reg.required_value ? ` (required: ${reg.required_value})` : ""}
                                            </div>
                                          )}
                                          {selectedModelId !== 'dutch-bros' && selectedModelId !== 'bne' && reg.notes && (
                                            <div className="text-xs text-gray-600 italic">
                                              {reg.notes}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
            )}
          </>
        )}

        {/* 604 Section hidden - was "Scanned in AU units for System Capability" */}
        {false && parentIds.filter(id => id === "604").map((parentId) => {
          const subsections = groupedRegulations[parentId];
          const subsectionIds = Object.keys(subsections).sort(naturalSort);
          const sectionId = `section-${parentId}`;
          const isExpanded = expandedSections.has(sectionId);

          // Get all regulations for this parent section to determine title
          const allRegsInSection = Object.values(subsections).flat();
          const sectionTitle = getSectionTitle(allRegsInSection, selectedModelId);

          return (
            <React.Fragment key={parentId}>
              <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                Scanned in AU units for System Capability
                <div className="relative group">
                  <svg
                    className="w-4 h-4 text-blue-500 cursor-help"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[300px] max-w-[480px] z-50">
                    Scanned by Hero, but no immediate action items found
                    <div className="absolute top-full right-4 -mt-1">
                      <div className="border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </h2>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => toggleSection(sectionId)}
                      className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors"
                    >
                      {isExpanded ? (
                        <svg
                          className="w-4 h-4 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      )}
                      <span>{sectionTitle}</span>
                      <span className="text-xs text-gray-500 font-normal">
                        ({allRegsInSection.length} regulations)
                      </span>
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 py-4 space-y-4">
                    {/* Subsections */}
                    {subsectionIds.map((subsectionId) => {
                      const regulations = subsections[subsectionId];
                      const subsectionKey = `${sectionId}-${subsectionId}`;
                      const isSubsectionExpanded =
                        expandedSubsections.has(subsectionKey);
                      const subsectionTitle = getSubsectionTitle(regulations, regulationDefinitions, selectedModelId);

                      return (
                        <div
                          key={subsectionId}
                          className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleSubsection(subsectionKey)}
                            className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 flex items-center gap-2 transition-colors text-left"
                          >
                            {isSubsectionExpanded ? (
                              <svg
                                className="w-4 h-4 text-gray-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4 text-gray-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            )}
                            <span className="text-sm font-semibold text-gray-900">
                              {subsectionTitle}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({regulations.length} regulations)
                            </span>
                          </button>

                          {isSubsectionExpanded && (
                            <div className="p-3 space-y-2">
                              {regulations.map((reg) => {
                                const isRegExpanded = expandedRegulations.has(
                                  reg.regulation_id
                                );

                                return (
                                  <div
                                    key={reg.regulation_id}
                                    className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                                  >
                                    <button
                                      onClick={() =>
                                        toggleRegulation(reg.regulation_id)
                                      }
                                      className="w-full px-3 py-2 bg-white hover:bg-gray-50 flex items-center gap-2 transition-colors text-left"
                                    >
                                      {isRegExpanded ? (
                                        <svg
                                          className="w-4 h-4 text-gray-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                          />
                                        </svg>
                                      ) : (
                                        <svg
                                          className="w-4 h-4 text-gray-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                          />
                                        </svg>
                                      )}
                                      <span className="text-xs font-mono text-gray-600">
                                        {formatRegulationIdForDisplay(
                                          reg.regulation_id,
                                          selectedModelId
                                        )}
                                      </span>
                                      <span className="text-sm font-medium text-gray-900 flex-1">
                                        {reg.title || "No title"}
                                      </span>
                                    </button>

                                    {isRegExpanded && (
                                      <div className="p-3 bg-gray-50 border-t border-gray-200">
                                        <div className="text-xs text-gray-700">
                                          <span className="font-medium">
                                            Requirement:
                                          </span>{" "}
                                          {reg.requirement}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}

        {/* 404 Section - Display all regulations from ADA_404_doors.json */}
        {ada404Regulations.length > 0 && (
          <React.Fragment>
            {selectedModelId !== 'bne' && (
            <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              Chapter 404 - Doors, Doorways and Gates
              <div className="relative group">
                <svg
                  className="w-4 h-4 text-blue-500 cursor-help"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[300px] max-w-[480px] z-50">
                  Regulations from ADA Section 404 - Doors, Doorways and Gates
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            </h2>
            )}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleSection("section-404")}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors"
                  >
                    {expandedSections.has("section-404") ? (
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                    <span>Chapter 404 - Doors, Doorways and Gates</span>
                    <span className="text-xs text-gray-500 font-normal">
                      ({Object.values(grouped404Regulations).flat().length} regulations)
                    </span>
                  </button>
                </div>
              </div>

              {expandedSections.has("section-404") && (
                <div className="px-4 py-4 space-y-4">
                  {/* Subsections */}
                  {Object.keys(grouped404Regulations)
                    .sort(naturalSort)
                    .map((subsectionId) => {
                      const regulations = grouped404Regulations[subsectionId];
                      const subsectionKey = `section-404-${subsectionId}`;
                      const isSubsectionExpanded =
                        expandedSubsections.has(subsectionKey);
                      const subsectionTitle = getSubsectionTitle(regulations, regulationDefinitions, selectedModelId);

                      return (
                        <div
                          key={subsectionId}
                          className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleSubsection(subsectionKey)}
                            className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 flex items-center gap-2 transition-colors text-left"
                          >
                            {isSubsectionExpanded ? (
                              <svg
                                className="w-4 h-4 text-gray-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4 text-gray-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            )}
                            <span className="text-sm font-semibold text-gray-900">
                              {subsectionTitle}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({regulations.length} regulations)
                            </span>
                          </button>

                          {isSubsectionExpanded && (
                            <div className="p-3 space-y-2">
                              {regulations.map((reg) => {
                                const isRegExpanded = expandedRegulations.has(
                                  reg.regulation_id
                                );

                                return (
                                  <div
                                    key={reg.regulation_id}
                                    className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                                  >
                                    <button
                                      onClick={() =>
                                        toggleRegulation(reg.regulation_id)
                                      }
                                      className="w-full px-3 py-2 bg-white hover:bg-gray-50 flex items-center gap-2 transition-colors text-left"
                                    >
                                      {isRegExpanded ? (
                                        <svg
                                          className="w-4 h-4 text-gray-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                          />
                                        </svg>
                                      ) : (
                                        <svg
                                          className="w-4 h-4 text-gray-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                          />
                                        </svg>
                                      )}
                                      <span className="text-xs font-mono text-gray-600">
                                        {formatRegulationIdForDisplay(
                                          reg.regulation_id,
                                          selectedModelId
                                        )}
                                      </span>
                                      <span className="text-sm font-medium text-gray-900 flex-1">
                                        {reg.title || "No title"}
                                      </span>
                                    </button>

                                    {isRegExpanded && (
                                      <div className="p-3 bg-gray-50 border-t border-gray-200">
                                        <div className="text-xs text-gray-700">
                                          <span className="font-medium">
                                            Requirement:
                                          </span>{" "}
                                          {reg.requirement}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}
